<template>
    <div class="timer-page">
        <div class="timer-header" v-if="station">
            <span class="station-code" v-if="station.code">{{ station.code }}</span>
            <span class="station-title">{{ station.title }}</span>
        </div>

        <button class="settings-toggle" @click="settingsOpen = true" :aria-label="$t('settings')">
            <b-icon icon="list"></b-icon>
        </button>

        <div class="timer-wrap">
            <div class="controls">
                <div class="btn-row">
                    <button class="btn btn-primary" @click="start" :disabled="startDisabled" :aria-label="$t('start')">
                        <b-icon icon="play-fill"></b-icon> {{ $t('start') }}
                    </button>
                    <button class="btn btn-secondary" @click="pause" :disabled="pauseDisabled" :aria-label="$t('pause')">
                        <b-icon icon="pause-fill"></b-icon> {{ $t('pause') }}
                    </button>
                    <button class="btn btn-secondary" @click="reset" :aria-label="$t('reset')">
                        <b-icon icon="arrow-counterclockwise"></b-icon> {{ $t('reset') }}
                    </button>
                </div>
            </div>

            <div class="clock-wrapper" ref="clockWrapper">
                <canvas ref="canvas"></canvas>
            </div>
        </div>

        <div class="settings-backdrop" v-if="settingsOpen" @click="settingsOpen = false"></div>
        <div class="settings-panel" :class="{ open: settingsOpen }">
            <div class="settings-panel-header">
                <span>{{ $t('settings') }}</span>
                <button class="settings-close" @click="settingsOpen = false" :aria-label="$t('close')">
                    <b-icon icon="x"></b-icon>
                </button>
            </div>
            <div class="settings-panel-body">
                <div class="field-group">
                    <label for="timer-mins">{{ $t('minutes') }}</label>
                    <input type="number" id="timer-mins" ref="minsIn" min="0" max="99" :value="initialMins" @change="onInputChange" @keydown.enter.prevent="onInputChange" />
                </div>
                <div class="field-group">
                    <label for="timer-secs">{{ $t('seconds') }}</label>
                    <input type="number" id="timer-secs" ref="secsIn" min="0" max="59" value="0" @change="onInputChange" @keydown.enter.prevent="onInputChange" />
                </div>
                <div class="field-group">
                    <label for="ambience-select">{{ $t('background-sound') }}</label>
                    <select id="ambience-select" v-model="selectedAmbience" @change="onAmbienceChange">
                        <option value="">{{ $t('none') }}</option>
                        <option v-for="opt in ambienceOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                </div>
                <div class="field-group">
                    <label for="ambience-volume">
                        <b-icon icon="volume-up-fill"></b-icon> {{ $t('ambience-volume') }}
                    </label>
                    <input type="range" id="ambience-volume" min="0" max="1" step="0.05" v-model.number="ambienceVolume" @input="onAmbienceVolumeChange" />
                </div>
                <div class="field-group">
                    <label for="timer-volume">
                        <b-icon icon="bell-fill"></b-icon> {{ $t('timer-sound-volume') }}
                    </label>
                    <input type="range" id="timer-volume" min="0" max="1" step="0.05" v-model.number="timerVolume" />
                </div>
                <div class="field-group">
                    <label for="remaining-alert-select">{{ $t('remaining-alert') }}</label>
                    <select id="remaining-alert-select" v-model="remainingAlertMode">
                        <option v-for="opt in availableRemainingAlertOptions" :key="opt.value" :value="opt.value">{{ $t(opt.labelKey) }}</option>
                    </select>
                </div>
                <div class="field-group">
                    <b-form-checkbox v-model="useHalfway" :unchecked-value="false" :value="true">
                        {{ $t('halfway-alert') }}
                    </b-form-checkbox>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            station: null,
            initialMins: 1,

            // timer state
            total: 60,
            remaining: 60,
            running: false,
            startDisabled: false,
            pauseDisabled: true,
            labelText: '1:00',

            // ambience
            ambienceOptions: [
                { value: 'hospital1', label: 'Hospital 1', file: '/sounds/ambience/hospital1.mp3' },
                { value: 'hospital2', label: 'Hospital 2', file: '/sounds/ambience/hospital2.mp3' },
                { value: 'hospital3', label: 'Hospital 3', file: '/sounds/ambience/hospital3.mp3' },
                { value: 'hospital4', label: 'Hospital 4', file: '/sounds/ambience/hospital4.mp3' },
            ],
            selectedAmbience: '',
            ambienceVolume: 0.5,
            timerVolume: 1,

            settingsOpen: false,

            // milestone alerts
            useHalfway: true,
            remainingAlertMode: 'none',
            remainingAlertOptionDefs: [
                { value: 'none', labelKey: 'none', minTotal: 0 },
                { value: '25pct', labelKey: 'alert-25-percent', minTotal: 0 },
                { value: '5min', labelKey: 'alert-5-minutes', minTotal: 5 * 60 * 2 },
                { value: '10min', labelKey: 'alert-10-minutes', minTotal: 10 * 60 * 2 },
                { value: '15min', labelKey: 'alert-15-minutes', minTotal: 15 * 60 * 2 },
            ],
        };
    },

    computed: {
        availableRemainingAlertOptions() {
            return this.remainingAlertOptionDefs.filter(opt => this.total > opt.minTotal);
        },
    },

    watch: {
        total() {
            if (!this.availableRemainingAlertOptions.some(opt => opt.value === this.remainingAlertMode)) {
                this.remainingAlertMode = 'none';
            }
        },
    },

    created() {
        // Non-reactive internals stored directly on the instance
        this.raf = null;
        this.lastTs = null;
        this.firedMilestones = new Set();
        this.resizeObserver = null;
        this.ambienceAudio = null;
    },

    mounted() {
        this.fetchStation().then(() => {
            this.resize();
            this.draw();
        });

        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(this.$refs.clockWrapper);

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.draw);
    },

    beforeDestroy() {
        this.cancelRaf();
        this.stopAmbience();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.draw);
    },

    methods: {
        fetchStation() {
            if (!this.$route.params.id) return Promise.resolve();
            return this.$cfetch(`${this.$settings.APIBaseURL}/osce/stations/get`, {
                data: { id: this.$route.params.id }
            })
            .then(res => {
                if (res.status !== 'SUCCESS') return;
                this.station = res.result;
                const mins = parseInt(res.result.duration) || 1;
                this.initialMins = mins;
                this.total = mins * 60;
                this.remaining = this.total;
                this.updateLabel();
                this.draw();
            })
            .catch(err => this.$log.error.call(this, err));
        },

        // ── drawing ──────────────────────────────────────────────

        resize() {
            const canvas = this.$refs.canvas;
            const wrapper = this.$refs.clockWrapper;
            if (!canvas || !wrapper) return;
            const size = Math.min(wrapper.clientWidth, wrapper.clientHeight);
            if (size <= 0) return;
            canvas.width = size;
            canvas.height = size;
            this.draw();
        },

        isDark() {
            return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        },

        colors() {
            return this.isDark()
                ? { track: '#3b82f6', fill: '#d3d1c7', hand: '#F5821F', face: '#252523' }
                : { track: '#3b82f6', fill: '#d3d1c7', hand: '#D85A30', face: '#f7f5f0' };
        },

        draw() {
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const W = canvas.width, H = canvas.height;
            if (!W || !H) return;
            const cx = W / 2, cy = H / 2;
            const R = W * 0.39;
            const c = this.colors();

            ctx.clearRect(0, 0, W, H);

            const progress = this.total > 0 ? (this.total - this.remaining) / this.total : 1;
            const startAngle = -Math.PI / 2;
            const endAngle = startAngle + Math.PI * 2 * progress;

            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, Math.PI * 2);
            ctx.fillStyle = c.face;
            ctx.fill();

            const trackWidth = W * 0.05;

            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, Math.PI * 2);
            ctx.strokeStyle = c.track;
            ctx.lineWidth = trackWidth;
            ctx.stroke();

            if (progress > 0) {
                ctx.beginPath();
                ctx.arc(cx, cy, R, startAngle, endAngle);
                ctx.strokeStyle = c.fill;
                ctx.lineWidth = trackWidth;
                ctx.lineCap = 'butt';
                ctx.stroke();
            }

            for (let i = 0; i < 60; i++) {
                const a = (i / 60) * Math.PI * 2 - Math.PI / 2;
                const isMajor = i % 5 === 0;
                const r0 = isMajor ? R - W * 0.07 : R - W * 0.05;
                const r1 = R - W * 0.093;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
                ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
                ctx.strokeStyle = c.track;
                ctx.lineWidth = isMajor ? 2 : 1;
                ctx.lineCap = 'round';
                ctx.stroke();
            }

            const handAngle = startAngle + Math.PI * 2 * progress;
            const handLen = R - W * 0.057;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(handAngle) * handLen, cy + Math.sin(handAngle) * handLen);
            ctx.strokeStyle = c.hand;
            ctx.lineWidth = Math.max(2, W * 0.011);
            ctx.lineCap = 'round';
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(cx, cy, W * 0.018, 0, Math.PI * 2);
            ctx.fillStyle = c.hand;
            ctx.fill();

            // Time remaining text on the face
            const displaySecs = Math.ceil(this.remaining);
            const m = Math.floor(displaySecs / 60);
            const s = displaySecs % 60;
            const timeStr = m + ':' + String(s).padStart(2, '0');
            ctx.font = `bold ${Math.round(W * 0.13)}px ui-monospace, monospace`;
            ctx.fillStyle = this.isDark() ? '#e5e3d8' : '#333';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(timeStr, cx, cy + R * 0.45);

            this.updateLabel();
        },

        updateLabel() {
            const displaySecs = Math.ceil(this.remaining);
            const m = Math.floor(displaySecs / 60);
            const s = displaySecs % 60;
            this.labelText = m + ':' + String(s).padStart(2, '0');
        },

        // ── milestones & sound ────────────────────────────────────

        checkMilestones() {
            if (this.total <= 0) return;
            const elapsedPct = (this.total - this.remaining) / this.total * 100;

            if (this.useHalfway && elapsedPct >= 50 && !this.firedMilestones.has('halfway')) {
                this.firedMilestones.add('halfway');
                this.playSound('halfway');
            }

            if (this.remainingAlertMode !== 'none' && !this.firedMilestones.has('remainingAlert')) {
                const remainingThresholds = { '5min': 5 * 60, '10min': 10 * 60, '15min': 15 * 60 };
                const shouldFire = this.remainingAlertMode === '25pct'
                    ? elapsedPct >= 75
                    : this.remaining <= remainingThresholds[this.remainingAlertMode];
                if (shouldFire) {
                    this.firedMilestones.add('remainingAlert');
                    this.playSound(this.remainingAlertMode === '25pct' ? 'lastQuarter' : this.remainingAlertMode);
                }
            }

            if (elapsedPct >= 100 && !this.firedMilestones.has('done')) {
                this.firedMilestones.add('done');
                this.playSound('done');
            }
        },

        playSound(type) {
            const files = {
                start:       '/sounds/bell_start_session.mp3',
                halfway:     '/sounds/half_way.mp3',
                lastQuarter: '/sounds/last_quarter.mp3',
                '5min':      '/sounds/5_minutes_remaining.mp3',
                '10min':     '/sounds/10_minutes_remaining.mp3',
                '15min':     '/sounds/15_minutes_remaining.mp3',
                done:        '/sounds/bell_session_ended.mp3',
                paused:      '/sounds/beep_session_paused.mp3',
                resumed:     '/sounds/bell_session_resumed.mp3',
            };
            const audio = new Audio(files[type]);
            audio.volume = this.timerVolume;
            audio.play().catch(() => {});
        },

        // ── ambience ──────────────────────────────────────────────

        onAmbienceChange() {
            this.stopAmbience();
            if (!this.selectedAmbience) return;
            const opt = this.ambienceOptions.find(o => o.value === this.selectedAmbience);
            if (!opt) return;
            const audio = new Audio(opt.file);
            audio.loop = true;
            audio.volume = this.ambienceVolume;
            audio.play().catch(() => {});
            this.ambienceAudio = audio;
        },

        onAmbienceVolumeChange() {
            if (this.ambienceAudio) this.ambienceAudio.volume = this.ambienceVolume;
        },

        stopAmbience() {
            if (this.ambienceAudio) {
                this.ambienceAudio.pause();
                this.ambienceAudio.src = '';
                this.ambienceAudio = null;
            }
        },

        // ── tick loop ─────────────────────────────────────────────

        tick(ts) {
            if (!this.lastTs) this.lastTs = ts;
            const delta = ts - this.lastTs;
            this.lastTs = ts;
            this.remaining = Math.max(0, this.remaining - delta / 1000);
            this.checkMilestones();
            this.draw();
            if (this.remaining <= 0) {
                this.running = false;
                this.raf = null;
                this.lastTs = null;
                this.startDisabled = false;
                this.pauseDisabled = true;
                this.pulse();
                return;
            }
            this.raf = requestAnimationFrame(ts => this.tick(ts));
        },

        cancelRaf() {
            if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
        },

        pulse() {
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            let count = 0;
            const id = setInterval(() => {
                canvas.style.opacity = count % 2 === 0 ? '0.4' : '1';
                count++;
                if (count >= 6) { clearInterval(id); canvas.style.opacity = '1'; }
            }, 200);
        },

        // ── controls ──────────────────────────────────────────────

        start() {
            if (this.remaining <= 0 || this.running) return;
            const elapsed = (this.total - this.remaining) / this.total * 100;
            this.playSound(elapsed === 0 ? 'start' : 'resumed');
            this.running = true;
            this.lastTs = null;
            this.startDisabled = true;
            this.pauseDisabled = false;
            this.raf = requestAnimationFrame(ts => this.tick(ts));
        },

        pause() {
            if (!this.running) return;
            this.running = false;
            this.cancelRaf();
            this.lastTs = null;
            this.startDisabled = false;
            this.pauseDisabled = true;
            this.playSound('paused');
        },

        reset() {
            this.running = false;
            this.cancelRaf();
            this.lastTs = null;
            const m = Math.max(0, parseInt(this.$refs.minsIn.value) || 0);
            const s = Math.max(0, Math.min(59, parseInt(this.$refs.secsIn.value) || 0));
            this.initialMins = m;
            this.total = m * 60 + s;
            this.remaining = this.total;
            this.firedMilestones.clear();
            this.startDisabled = false;
            this.pauseDisabled = true;
            if (this.$refs.canvas) this.$refs.canvas.style.opacity = '1';
            this.draw();
        },

        onInputChange() {
            if (!this.running) {
                const m = Math.max(0, parseInt(this.$refs.minsIn.value) || 0);
                const s = Math.max(0, Math.min(59, parseInt(this.$refs.secsIn.value) || 0));
                this.initialMins = m;
                this.total = m * 60 + s;
                this.remaining = this.total;
                this.firedMilestones.clear();
                this.draw();
            }
        },
    },
};
</script>

<style scoped>
.timer-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100vh;
    overflow: hidden;
    padding: 0.4rem 1rem 0.15rem;
    box-sizing: border-box;
}

.timer-header {
    flex-shrink: 0;
    text-align: center;
    margin-bottom: 0.1rem;
}

.station-code {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-secondary, #6c757d);
    margin-right: 0.5rem;
}

.station-title {
    font-size: 1.25rem;
    font-weight: 700;
}

.timer-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: 100%;
    flex: 1;
    min-height: 0;
}

.controls {
    flex-shrink: 0;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
}

.field-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.field-group label {
    font-size: 12px;
    color: var(--color-text-secondary, #6c757d);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.field-group input[type=number] {
    width: 64px;
    text-align: center;
    padding: 0.25rem 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
}

.field-group select {
    padding: 0.25rem 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    background-color: var(--color-bg, #fff);
    color: inherit;
}

.field-group input[type=range] {
    width: 100px;
}

.btn-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
}

.clock-wrapper {
    flex: 1;
    min-height: 0;
    width: 100%;
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.clock-wrapper canvas {
    display: block;
}

.settings-toggle {
    position: fixed;
    top: 0.75rem;
    right: 1rem;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--color-text-secondary, #6c757d);
    font-size: 1.25rem;
    cursor: pointer;
}

.settings-toggle:hover {
    background: rgba(0, 0, 0, 0.06);
}

.settings-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 1002;
}

.settings-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    max-width: 85vw;
    background: var(--color-bg, #fff);
    box-shadow: -2px 0 12px rgba(0, 0, 0, 0.2);
    z-index: 1003;
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.25s ease;
    overflow-y: auto;
}

.settings-panel.open {
    transform: translateX(0);
}

.settings-panel-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    font-weight: 700;
    border-bottom: 1px solid var(--color-text-secondary, #ced4da);
}

.settings-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: inherit;
    font-size: 1.1rem;
    cursor: pointer;
}

.settings-close:hover {
    background: rgba(0, 0, 0, 0.06);
}

.settings-panel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
    padding: 1rem;
}

.settings-panel-body .field-group {
    align-items: flex-start;
}

.settings-panel-body .field-group select,
.settings-panel-body .field-group input[type=range] {
    width: 100%;
}

</style>
